import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Check, Loader2, Package } from 'lucide-react'
import { useEffect, useState } from 'react'
import { type AdminModule, getAdminModules, PlatformApiError, updateAdminModule } from '@/lib/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/platform/modules')({
  component: PlatformModulesPage,
})

function PlatformModulesPage() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'loaded'; modules: AdminModule[] }
    | { status: 'error'; message: string }
  >({ status: 'loading' })

  // Per-row mutation state so multiple toggles can happen in parallel
  // without stepping on each other (the set is keyed by module key).
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set())
  const [mutationError, setMutationError] = useState<string | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    getAdminModules(ctrl.signal)
      .then((modules) => setState({ status: 'loaded', modules }))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message =
          err instanceof PlatformApiError && err.message ? err.message : 'Failed to load modules'
        setState({ status: 'error', message })
      })
    return () => ctrl.abort()
  }, [])

  async function toggleRequiresLicense(module: AdminModule) {
    if (state.status !== 'loaded') return
    const nextValue = !module.requiresLicense

    // Optimistic update — swap the value into local state
    // immediately so the toggle feels instant, then roll back on
    // failure. The full row is returned from the server on success
    // so we re-sync with authoritative state at the end.
    const prevModules = state.modules
    setState({
      status: 'loaded',
      modules: prevModules.map((m) =>
        m.key === module.key ? { ...m, requiresLicense: nextValue } : m,
      ),
    })
    setBusyKeys((s) => new Set(s).add(module.key))
    setMutationError(null)

    try {
      const updated = await updateAdminModule(module.key, { requiresLicense: nextValue })
      setState((s) => {
        if (s.status !== 'loaded') return s
        return {
          status: 'loaded',
          modules: s.modules.map((m) => (m.key === module.key ? updated : m)),
        }
      })
    } catch (err) {
      // Roll back
      setState({ status: 'loaded', modules: prevModules })
      const message =
        err instanceof PlatformApiError && err.message
          ? err.message
          : `Failed to update ${module.key}`
      setMutationError(message)
    } finally {
      setBusyKeys((s) => {
        const next = new Set(s)
        next.delete(module.key)
        return next
      })
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">Module catalog</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Every module on the platform. Toggle{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">requires license</code> to activate
          the Path B filter — licensed tenants will see the module, unlicensed tenants won't.
        </p>
      </header>

      {mutationError ? (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {mutationError}
        </div>
      ) : null}

      {state.status === 'loading' ? <LoadingState /> : null}
      {state.status === 'error' ? <ErrorState message={state.message} /> : null}
      {state.status === 'loaded' && state.modules.length === 0 ? <EmptyState /> : null}
      {state.status === 'loaded' && state.modules.length > 0 ? (
        <ModuleTable
          modules={state.modules}
          busyKeys={busyKeys}
          onToggleRequiresLicense={toggleRequiresLicense}
        />
      ) : null}
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card/60 py-12 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading modules…
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
      {message}
    </div>
  )
}

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center"
    >
      <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
        <Package className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-lg font-semibold">No modules registered</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        The module_catalog table is empty. Run{' '}
        <code className="rounded bg-muted px-1 py-0.5 text-xs">sql/management-db-modules.sql</code>{' '}
        to seed the built-in modules.
      </p>
    </motion.div>
  )
}

function ModuleTable({
  modules,
  busyKeys,
  onToggleRequiresLicense,
}: {
  modules: AdminModule[]
  busyKeys: Set<string>
  onToggleRequiresLicense: (m: AdminModule) => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Module</th>
              <th className="px-4 py-2.5 text-left font-semibold">Key</th>
              <th className="px-4 py-2.5 text-left font-semibold">Source</th>
              <th className="px-4 py-2.5 text-left font-semibold">Requires license</th>
              <th className="px-4 py-2.5 text-left font-semibold">Price</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {modules.map((module) => {
              const busy = busyKeys.has(module.key)
              return (
                <tr key={module.key} className="hover:bg-accent/40">
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2">
                      <div className="font-medium">{module.name}</div>
                      {module.isCore ? (
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary ring-1 ring-primary/25">
                          Core
                        </span>
                      ) : null}
                    </div>
                    {module.description ? (
                      <div className="mt-0.5 max-w-md text-xs text-muted-foreground">
                        {module.description}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{module.key}</code>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <SourceBadge source={module.source} />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <Toggle
                      checked={module.requiresLicense}
                      busy={busy}
                      disabled={module.isCore}
                      onChange={() => onToggleRequiresLicense(module)}
                      title={
                        module.isCore
                          ? 'Core modules cannot be gated by license'
                          : module.requiresLicense
                            ? 'License required — click to make free'
                            : 'Free for all tenants — click to require license'
                      }
                    />
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                    {formatPrice(module.monthlyPriceCents)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  )
}

/**
 * Tiny toggle switch. No dependency — just a styled button that
 * shows a spinner when mid-flight and a gradient background when
 * on. Used here for the requires_license column.
 */
function Toggle({
  checked,
  busy,
  disabled,
  onChange,
  title,
}: {
  checked: boolean
  busy: boolean
  disabled?: boolean
  onChange: () => void
  title?: string
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      aria-pressed={checked}
      disabled={disabled || busy}
      title={title}
      className={cn(
        'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        checked ? 'bg-primary' : 'bg-muted ring-1 ring-border',
        disabled && 'opacity-50 cursor-not-allowed',
        busy && 'opacity-60',
      )}
    >
      <span
        className={cn(
          'inline-flex h-4 w-4 items-center justify-center rounded-full bg-background shadow-sm transition-transform',
          checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
        )}
      >
        {busy ? (
          <Loader2 className="h-2.5 w-2.5 animate-spin text-muted-foreground" />
        ) : checked ? (
          <Check className="h-2.5 w-2.5 text-primary" />
        ) : null}
      </span>
    </button>
  )
}

function SourceBadge({ source }: { source: string }) {
  const label =
    source === 'builtin'
      ? 'Built-in'
      : source === 'marketplace'
        ? 'Marketplace'
        : source === 'custom_tenant'
          ? 'Custom'
          : source
  const tone =
    source === 'builtin'
      ? 'bg-muted text-muted-foreground ring-border'
      : source === 'marketplace'
        ? 'bg-primary/10 text-primary ring-primary/25'
        : 'bg-chart-3/10 text-chart-3 ring-chart-3/25'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        tone,
      )}
    >
      {label}
    </span>
  )
}

function formatPrice(cents: string | null): string {
  if (cents === null || cents === undefined || cents === '') return 'Free'
  const asNumber = Number(cents)
  if (Number.isNaN(asNumber)) return 'Free'
  if (asNumber === 0) return 'Free'
  return `€${(asNumber / 100).toFixed(2)} / mo`
}
